// src/components/tables/TeamMembersTable.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { apiFetch } from '../../utils/api';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import CardSectionLabel from '../cards/CardSectionLabel';
import CallIcon from '@mui/icons-material/Call';
import { alpha } from '@mui/material/styles';
import { Logger } from '../../utils/logger';
import EmployeeNameLink from '../common/EmployeeNameLink';
import { TABS } from '../cards/EmployeeExpandedView';

const logger = new Logger('TeamMembersTable');

/**
 * TeamMembersTable component displays team members assigned to a project
 * in a tabular format with name, role, and contact options.
 */
const TeamMembersTable = ({
  team = [],
  projectId = null,
  limit = 5, // Default to 5 team members
  showMore = false,
  onShowMoreClick,
  onTimesheetsClick
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Fetch team members from API if not provided as props
  useEffect(() => {
    if (team && team.length > 0) {
      // Use the team provided via props
      setTeamMembers(team);
      return;
    }
    
    if (!projectId) {
      // If no project ID and no team, nothing to show
      return;
    }
    
    // Fetch team members from API
    const fetchTeamMembers = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Format project ID for API call (always pass with P prefix)
        const formattedId = projectId.toString();
        const apiProjectId = formattedId.startsWith('P') ? formattedId : `P${formattedId}`;
        
        logger.debug(`Fetching team members for project ID: ${apiProjectId}`);

        // Fetch assignments
        const response = await apiFetch(`projects/${apiProjectId}/assignments?active_only=false`);

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        const data = await response.json();

        if (data && Array.isArray(data)) {
          logger.debug('Team assignments received:', data);
          
          // Group assignments by employee_id
          const employeeMap = new Map();

          // First pass - collect employee data and roles
          for (const assignment of data) {
            const employeeId = assignment.employee_id;
            if (!employeeId) continue;
            
            if (!employeeMap.has(employeeId)) {
              employeeMap.set(employeeId, {
                employee_id: employeeId,
                roles: [assignment.role || 'Team Member'],
                assignment_data: [assignment]
              });
            } else {
              const existingEmployee = employeeMap.get(employeeId);
              const role = assignment.role || 'Team Member';
              if (!existingEmployee.roles.includes(role)) {
                existingEmployee.roles.push(role);
              }
              existingEmployee.assignment_data.push(assignment);
            }
          }
          
          // Second pass - create final team member objects
          // Use employee_name from assignment data (already resolved by backend) — avoids N+1 calls
          const processedTeam = Array.from(employeeMap.values()).map((employeeInfo) => {
            const assignmentName = employeeInfo.assignment_data[0]?.employee_name;
            const employeeName = (assignmentName && assignmentName !== employeeInfo.employee_id)
              ? assignmentName
              : `Employee ${employeeInfo.employee_id.replace('EMP', '')}`;
            const employeeData = {};
            
            // Get the primary assignment (first one, or one with PM role)
            const pmAssignment = employeeInfo.assignment_data.find(a => 
              (a.role || '').toLowerCase().includes('project manager')
            );
            const primaryAssignment = pmAssignment || employeeInfo.assignment_data[0];
            
            // Determine if this person is a project manager
            const isPM = employeeInfo.roles.some(role => 
              role.toLowerCase().includes('project manager')
            );
            
            // Process roles - remove "Team Member" if there are other roles
            let roles = [...employeeInfo.roles];
            if (roles.length > 1 && roles.includes('Team Member')) {
              roles = roles.filter(role => role !== 'Team Member');
            }
            
            // Create team member object with fallbacks for missing data
            return {
              name: employeeName,
              role: roles.join(', '), // Join all roles with comma
              title: employeeData.title || 'Team Member',
              hours: primaryAssignment.total_hours_assigned || 
                    (primaryAssignment.weekly_hours ? 
                      (primaryAssignment.weekly_hours * (primaryAssignment.role_duration_weeks || 0)) : 0) || 0,
              status: primaryAssignment.assignment_status || 'Active',
              employee_id: employeeInfo.employee_id,
              employee_data: employeeData,
              email: employeeData.email || null,
              phone: employeeData.phone || null,
              start_date: primaryAssignment.role_start_date,
              end_date: primaryAssignment.role_end_date,
              is_project_manager: isPM,
              is_terminated: employeeData.current_status === 'Terminated'
            };
          });

          // Sort: Project Manager first, then others
          processedTeam.sort((a, b) => {
            if (a.is_project_manager && !b.is_project_manager) return -1;
            if (!a.is_project_manager && b.is_project_manager) return 1;
            return 0;
          });
          
          setTeamMembers(processedTeam);
        } else {
          logger.warn('No team data in response or invalid format:', data);
          setTeamMembers([]);
        }
      } catch (err) {
        logger.error('Error fetching team members:', err);
        setError('Failed to load team members');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeamMembers();
  }, [projectId, team, limit]);
  
  // Communication handler functions
  const handleEmail = (e, email, name) => {
    e.stopPropagation();
    if ((email || '').trim()) {
      window.location.href = `mailto:${email.trim()}`;
    } else {
      logger.debug(`No email available for ${name}`);
    }
  };
  
  const handleChat = (e, employeeId, name) => {
    e.stopPropagation();
    if (employeeId) {
      // Construct Teams chat URL with employee email
      // This is a placeholder - actual implementation depends on your system
      const email = `${employeeId.toLowerCase()}@mbco.com`;
      window.open(`https://teams.microsoft.com/l/chat/0/0?users=${email}`, '_blank');
    } else {
      logger.debug(`Cannot chat with ${name} - no employee ID`);
    }
  };
  
  const handleCall = (e, phone, name) => {
    e.stopPropagation();
    if ((phone || '').trim()) {
      window.location.href = `tel:${phone.trim()}`;
    } else {
      logger.debug(`No phone number available for ${name}`);
    }
  };
  
  // Show loading state
  if (loading) {
    return (
      <Box sx={{ px: 1.5, pt: 1.5 }}>
        <Typography sx={{ 
          fontSize: '0.8rem', 
          fontWeight: 600, 
          mb: 1, 
          textTransform: 'uppercase'
        }}>
          Team Members
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      </Box>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <Box sx={{ px: 1.5, pt: 1.5 }}>
        <Typography sx={{ 
          fontSize: '0.8rem', 
          fontWeight: 600, 
          mb: 1, 
          textTransform: 'uppercase'
        }}>
          Team Members
        </Typography>
        <Typography color="error" sx={{ fontSize: '0.7rem' }}>
          {error}
        </Typography>
      </Box>
    );
  }
  
  const displayedTeam = teamMembers.slice(0, limit);
  const hasMoreMembers = teamMembers.length > limit;
  
  return (
    <Box sx={{ px: 1.5, pt: 1.5 }}>
      <CardSectionLabel
        label="Team Members"
        action={(onShowMoreClick || onTimesheetsClick) ? (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {onTimesheetsClick && (
              <Typography onClick={onTimesheetsClick}
                sx={{ fontSize: '0.65rem', color: '#BB86FC', cursor: 'pointer' }}>
                Timesheets
              </Typography>
            )}
            {onShowMoreClick && (
              <Typography onClick={onShowMoreClick}
                sx={{ fontSize: '0.65rem', color: 'primary.main', cursor: 'pointer' }}>
                Show Details
              </Typography>
            )}
          </Box>
        ) : null}
      />
      
      <Table size="small" sx={{ mb: 0.5, backgroundColor: '#1A1A1A' }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#1A1A1A' }}>
            <TableCell>Name</TableCell>
            <TableCell>Role</TableCell>
            <TableCell align="right">Contact</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedTeam.length > 0 ? (
            displayedTeam.map((member, index) => (
              <TableRow key={index} sx={{ backgroundColor: '#1A1A1A' }}>
                <TableCell sx={{ backgroundColor: '#1A1A1A' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ color: 'text.primary', fontSize: '0.75rem' }}>
                      <EmployeeNameLink employee={member} tab={TABS.ENGAGEMENTS}>{member.name}</EmployeeNameLink>
                    </Typography>
                    {member.employee_id && (
                      <Typography sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>
                        {member.employee_id}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A' }}>
                  {member.role}
                </TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', textAlign: 'right' }}>
                  {member.is_terminated ? (
                    <Chip
                      label="Terminated"
                      size="small"
                      sx={{
                        bgcolor: alpha('#CF6679', 0.15),
                        color: '#CF6679',
                        fontSize: '0.65rem',
                        height: 20
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title={(member.email || '').trim() ? `Email ${member.name.split(' ')[0]}` : `No email available`}>
                        <IconButton 
                          size="small" 
                          sx={{ 
                            color: (member.email || '').trim() ? '#64B5F6' : 'rgba(100, 181, 246, 0.5)',
                            p: 0.5 
                          }}
                          onClick={(e) => handleEmail(e, member.email, member.name)}
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={`Chat with ${member.name.split(' ')[0]}`}>
                        <IconButton 
                          size="small" 
                          sx={{ color: '#BB86FC', p: 0.5 }}
                          onClick={(e) => handleChat(e, member.employee_id, member.name)}
                        >
                          <ChatIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={(member.phone || '').trim() ? `Call ${member.name.split(' ')[0]}` : `No phone number available`}>
                        <IconButton 
                          size="small" 
                          sx={{ 
                            color: (member.phone || '').trim() ? '#03DAC6' : 'rgba(3, 218, 198, 0.5)',
                            p: 0.5
                          }}
                          onClick={(e) => handleCall(e, member.phone, member.name)}
                        >
                          <CallIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow sx={{ backgroundColor: '#1A1A1A' }}>
              <TableCell colSpan={3} align="center" sx={{ backgroundColor: '#1A1A1A' }}>
                No team members assigned yet
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

TeamMembersTable.propTypes = {
  team: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    role: PropTypes.string,
    hours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    utilization: PropTypes.number,
    employee_id: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string
  })),
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  limit: PropTypes.number,
  showMore: PropTypes.bool,
  onShowMoreClick:   PropTypes.func,
  onTimesheetsClick: PropTypes.func
};

export default TeamMembersTable;
